/**
 * WatchMatchModal — iter63z'''''''''''''
 *
 * Small popup that surfaces per-match box-score deep links. Currently
 * surfaces ESPN only; designed to host additional providers later (e.g.
 * Fox box-score, broadcast finders) without needing UI changes.
 *
 * Used by:
 *   • ScheduleWidget rows (📋 icon button per row)
 *   • LiveGameStrip green LIVE banner (📋 icon next to the arrow)
 *
 * Spectator mode → callers gate this off entirely (button not rendered).
 */
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, ClipboardList } from "lucide-react";
import { getWatchProviders } from "@/lib/watchProviders";
import { openExternalLink } from "@/lib/externalLink";

const _matchLabel = (entry) => {
    const home = entry?.home_label || "TBD";
    const away = entry?.away_label || "TBD";
    return `${home} vs ${away}`;
};

const _matchTime = (entry) => {
    if (!entry?.scheduled_at) return null;
    try {
        return new Date(entry.scheduled_at).toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    } catch { return null; }
};

export function WatchMatchModal({ open, onClose, template, entry }) {
    const providers = getWatchProviders(template, entry);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent
                data-testid="watch-match-modal"
                className="max-w-md bg-zinc-950 border-2 border-zinc-800"
            >
                <DialogHeader>
                    <DialogTitle className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-cyber" /> Match box score
                    </DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500">
                        Jump to the full box score for this match.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-1 pb-1">
                    <p className="text-sm text-white font-bold leading-tight" data-testid="watch-match-title">
                        {_matchLabel(entry)}
                    </p>
                    {_matchTime(entry) && (
                        <p className="text-[11px] text-zinc-500" data-testid="watch-match-time">
                            {_matchTime(entry)}
                        </p>
                    )}
                </div>

                {(!providers || providers.length === 0) && (
                    <p className="text-xs text-zinc-500 italic" data-testid="watch-match-empty">
                        No box-score links available for this tournament yet.
                    </p>
                )}

                {providers && providers.length > 0 && (
                    <div className="space-y-2 pt-1">
                        {providers.map((p) => (
                            <a
                                key={p.key}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { e.preventDefault(); openExternalLink(p.url); }}
                                data-testid={`watch-link-${p.key}`}
                                className="flex items-center gap-3 p-3 rounded border-2 border-zinc-800 hover:border-cyber hover:bg-cyber/5 transition-colors group"
                            >
                                <ClipboardList className="w-4 h-4 text-cyber shrink-0" />
                                <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-bold text-white truncate">
                                        {p.label}
                                    </span>
                                    <span className="block text-[11px] text-zinc-500 truncate">
                                        {p.sub}
                                    </span>
                                </span>
                                <ExternalLink className="w-3.5 h-3.5 text-zinc-600 group-hover:text-cyber shrink-0" />
                            </a>
                        ))}
                    </div>
                )}

                <p className="text-[10px] text-zinc-600 italic pt-2 text-center">
                    Opens in a new tab. Box-score availability varies by region.
                </p>
            </DialogContent>
        </Dialog>
    );
}

export default WatchMatchModal;
